import React from 'react';
import './enterprise-report.css';

// 企业报告组件（spec §4.2）— AIQ 深蓝金边视觉。R2 跨班 / R3 全员。5 力 / 四阶 / 无素质。
// payload 契约不变（仅视觉重写）。防御式：全字段可选链 + 缺字段兜底，绝不抛。
// LLM 列表字段一律 asArray 归一（真机暴露教训）。

const L_GRADE_LABEL = {
  L1: 'L1 知晓尝试级',
  L2: 'L2 基础应用级',
  L3: 'L3 协同优化级',
  L4: 'L4 创新引领级',
};

function asArray(v) {
  if (Array.isArray(v)) return v;
  if (v == null || v === '') return [];
  return [v];
}

function scoreColor(score) {
  if (score == null) return '#64748b';
  if (score >= 75) return '#15803d';
  if (score >= 60) return '#1d4ed8';
  if (score >= 50) return '#d97706';
  return '#dc2626';
}

function heatCssClass(score) {
  if (score == null) return 'er-hot-none';
  if (score >= 85) return 'er-hot-1';
  if (score >= 70) return 'er-hot-2';
  if (score >= 60) return 'er-hot-3';
  if (score >= 50) return 'er-hot-4';
  return 'er-hot-5';
}

function scoreToLGrade(score) {
  if (score == null) return null;
  if (score >= 85) return 'L4';
  if (score >= 70) return 'L3';
  if (score >= 55) return 'L2';
  return 'L1';
}

function formatBeijingTime(iso) {
  if (!iso) return '';
  const s = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 19).replace('T', ' ');
  const bj = new Date(d.getTime() + 8 * 60 * 60 * 1000);
  const p = (n) => String(n).padStart(2, '0');
  return (
    `${bj.getUTCFullYear()}-${p(bj.getUTCMonth() + 1)}-${p(bj.getUTCDate())} ` +
    `${p(bj.getUTCHours())}:${p(bj.getUTCMinutes())}:${p(bj.getUTCSeconds())}`
  );
}

// ── ① 封面 ─────────────────────────────────────────────────────────────────

function Cover({ cover }) {
  const c = cover || {};
  return (
    <div className="er-page er-cover">
      <div className="er-brand-row">
        <div className="er-brand">QIANSHAO · AI LEADERSHIP</div>
        <div className="er-badge">★ ENTERPRISE EDITION ★</div>
      </div>
      <h1>前哨 AI 领导力测评报告<br /><span style={{ opacity: 0.7 }}>·企业版</span></h1>
      <div className="er-ent-name">{c.enterprise_name || '企业'}</div>
      <div className="er-meta-grid">
        <div className="er-meta-cell">
          <div className="er-meta-label">所属行业</div>
          <div className="er-meta-value" style={{ fontSize: 16 }}>{c.industry || '未指定'}</div>
        </div>
        <div className="er-meta-cell">
          <div className="er-meta-label">覆盖班级</div>
          <div className="er-meta-value">{c.class_count || 0}<small>个班</small></div>
        </div>
        <div className="er-meta-cell">
          <div className="er-meta-label">参与人数</div>
          <div className="er-meta-value">{c.headcount || 0}<small>人</small></div>
        </div>
        <div className="er-meta-cell">
          <div className="er-meta-label">报告生成</div>
          <div className="er-meta-value" style={{ fontSize: 16 }}>{c.report_date || ''}</div>
        </div>
      </div>
    </div>
  );
}

// ── ② 公司战略画像 ─────────────────────────────────────────────────────────

function Portrait({ overview, scheduling }) {
  const ov = overview || {};
  const sched = scheduling || {};
  const radar = Array.isArray(ov.company_dimension_radar) ? ov.company_dimension_radar : [];
  const layers = Array.isArray(ov.company_layer_distribution) ? ov.company_layer_distribution : [];
  const top5 = Array.isArray(ov.company_position_top5) ? ov.company_position_top5 : [];
  const completed = ov.completed_headcount || 0;
  const total = ov.total_unique_headcount || 0;
  const completionRate = total ? ((completed / total) * 100).toFixed(1) : '0.0';
  const totalLayered = layers.reduce((s, l) => s + (l?.count || 0), 0);
  const l4 = layers.find((l) => l?.l_grade === 'L4');

  return (
    <div className="er-page">
      <div className="er-page-mark">PART 01 · 公司战略画像</div>
      <h2 className="er-h2">全员综合画像</h2>

      {(sched.headline_tag || sched.comprehensive_interpretation) && (
        <div className="er-headline-card">
          {sched.headline_tag && <div className="er-headline-tag">{sched.headline_tag}</div>}
          {sched.comprehensive_interpretation && (
            <div className="er-headline-summary">{sched.comprehensive_interpretation}</div>
          )}
        </div>
      )}

      <div className="er-kpi-grid">
        <div className="er-kpi">
          <div className="er-kpi-label">总参与人数</div>
          <div className="er-kpi-value" style={{ color: '#0f766e' }}>{total}<span className="er-kpi-unit">人</span></div>
        </div>
        <div className="er-kpi">
          <div className="er-kpi-label">已完成率</div>
          <div className="er-kpi-value" style={{ color: '#15803d' }}>{completionRate}<span className="er-kpi-unit">%</span></div>
          <div className="er-kpi-trend">已完成 {completed} / {total}</div>
        </div>
        <div className="er-kpi">
          <div className="er-kpi-label">已完成人数</div>
          <div className="er-kpi-value" style={{ color: '#1a3a6b' }}>{completed}<span className="er-kpi-unit">人</span></div>
        </div>
        <div className="er-kpi">
          <div className="er-kpi-label">L4 创新引领</div>
          <div className="er-kpi-value" style={{ color: '#d4860b' }}>{l4?.count || 0}<span className="er-kpi-unit">人</span></div>
        </div>
      </div>

      {layers.length > 0 && (
        <div className="er-card">
          <h3 className="er-h3">全员四阶成熟度分布（L1–L4）</h3>
          <div className="er-l-stack">
            {layers.map((l) => {
              const pct = totalLayered > 0 ? (l.count / totalLayered) * 100 : 0;
              const cls = `er-l${l.l_grade?.toLowerCase?.()?.replace('l', '') || '1'}`;
              return (
                <div key={l.l_grade} className={cls} style={{ width: `${pct}%`, minWidth: l.count > 0 ? 40 : 0 }}>
                  {l.count > 0 && `${l.l_grade} · ${l.count}`}
                </div>
              );
            })}
          </div>
          <div className="er-l-legend">
            {layers.map((l) => (
              <div key={l.l_grade} className="er-l-legend-item">
                <div className={`er-l-dot er-l${l.l_grade?.toLowerCase?.()?.replace('l', '') || '1'}`} />
                {L_GRADE_LABEL[l.l_grade] || l.l_grade} · {l.count} 人 · {l.pct}%
              </div>
            ))}
          </div>
        </div>
      )}

      {radar.length > 0 && (
        <div className="er-card">
          <h3 className="er-h3">全员五力均分</h3>
          {radar.map((d) => {
            const lGrade = scoreToLGrade(d?.avg_score);
            const isWeak = d?.avg_score != null && d.avg_score < 65;
            return (
              <div key={d?.code} className={`er-score-row${isWeak ? ' weak' : ''}`}>
                <span>{d?.name}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <b style={{ marginRight: 8, color: scoreColor(d?.avg_score) }}>{d?.avg_score ?? '—'}</b>
                  {lGrade && <span className={`er-lp er-lp-${lGrade}`}>{lGrade}</span>}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* 素质段已按 AILeader 约定移除；职位分布整行 */}
      {top5.length > 0 && (
        <div className="er-card">
          <h3 className="er-h3">学员职位分布 · Top 5</h3>
          {top5.map((p) => (
            <div key={p?.position} className="er-pos-row"><span>{p?.position}</span><b>{p?.count} 人</b></div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── ③ 跨班级横向对比（仅 R2）───────────────────────────────────────────────

function CrossClass({ overview }) {
  const ov = overview || {};
  const matrix = Array.isArray(ov.classes_kpi_matrix) ? ov.classes_kpi_matrix : [];
  const heatmap = Array.isArray(ov.cross_class_dimension_heatmap) ? ov.cross_class_dimension_heatmap : [];
  const dimCols = heatmap[0]?.cells || [];

  return (
    <div className="er-page">
      <div className="er-page-mark">PART 02 · 跨班级横向对比</div>
      <h2 className="er-h2">各班级整体表现对比</h2>

      <div className="er-card">
        <div className="er-muted" style={{ marginBottom: 14, fontSize: 12 }}>按团队均分降序排列</div>
        <table className="er-matrix">
          <thead>
            <tr>
              <th>班级</th><th>完成率</th>
              <th style={{ textAlign: 'right' }}>团队均分</th>
              <th>主要短板</th>
              <th style={{ textAlign: 'right' }}>风险条目</th>
            </tr>
          </thead>
          <tbody>
            {matrix.map((r) => (
              <tr key={r?.class_id}>
                <td>
                  <div className="er-class-name">{r?.class_name}</div>
                  <div className="er-class-size">{r?.size} 人</div>
                </td>
                <td className="er-muted">{r?.completion_rate}%</td>
                <td style={{ textAlign: 'right' }}><b style={{ color: scoreColor(r?.team_avg) }}>{r?.team_avg ?? '—'}</b></td>
                <td>
                  {asArray(r?.short_dims).length > 0
                    ? asArray(r?.short_dims).map((d) => <span key={d?.code || d?.name} className="er-short-chip">{d?.name || d}</span>)
                    : <span className="er-muted">—</span>}
                </td>
                <td style={{ textAlign: 'right' }}>{r?.risk_count ?? 0}</td>
              </tr>
            ))}
            {ov.classes_overflow?.count > 0 && (
              <tr className="er-overflow-row">
                <td colSpan={5}>另有 {ov.classes_overflow.count} 个班（均分约 {ov.classes_overflow.avg_team_avg ?? '—'}）未在上表展示</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {heatmap.length > 0 && (
        <div className="er-card">
          <h3 className="er-h3">各班级五力分项明细</h3>
          <div className="er-muted" style={{ marginBottom: 12, fontSize: 12 }}>每格 = 该班级在该维度的团队均分</div>
          <div className="er-heatmap-wrap">
            <table className="er-heatmap">
              <thead>
                <tr><th className="first">班级</th>{dimCols.map((c) => <th key={c?.code}>{c?.name}</th>)}</tr>
              </thead>
              <tbody>
                {heatmap.map((row) => (
                  <tr key={row?.class_id}>
                    <td className="label">{row?.class_name}</td>
                    {(row?.cells || []).map((c) => (
                      <td key={c?.code}><span className={`er-h-cell ${heatCssClass(c?.score)}`}>{c?.score ?? '—'}</span></td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── ④ 培训调度建议 ─────────────────────────────────────────────────────────

const PRIORITY_LABEL = { P0: '立即开展', P1: '本季跟进', P2: '后续观察' };

function Scheduling({ scheduling, isCrossClass }) {
  const sched = scheduling || {};
  const recs = Array.isArray(sched.scheduling_recommendations) ? sched.scheduling_recommendations : [];
  const quarters = Array.isArray(sched.quarterly_schedule) ? sched.quarterly_schedule : [];
  if (!recs.length && !quarters.length) return null;

  return (
    <div className="er-page">
      <div className="er-page-mark">PART 03 · 培训调度</div>
      <h2 className="er-h2">培训调度建议</h2>
      <p className="er-muted" style={{ marginBottom: 18, fontSize: 13 }}>
        {isCrossClass
          ? '基于横向数据识别多个班级共性能力洼地，按"合班可行性 × 紧急度"分配本期培训资源。'
          : '基于全员短板识别本期培训重点，按紧急度分配培训资源。'}
      </p>

      {recs.map((r, i) => (
        <div key={i} className={`er-sched-card ${r?.priority || 'P2'}`}>
          <div className="er-sched-header">
            <span className={`er-priority-pill ${r?.priority || 'P2'}`}>{PRIORITY_LABEL[r?.priority] || r?.priority || '—'}</span>
            <h3 className="er-sched-title">{r?.title}</h3>
            {r?.merge_strategy && <span className="er-merge-tag">{r.merge_strategy}</span>}
          </div>
          <div className="er-muted" style={{ fontSize: 13, marginBottom: 6 }}>
            覆盖维度 · {asArray(r?.covered_dims).join('、') || '—'}
          </div>
          {asArray(r?.target_classes).length > 0 && (
            <div style={{ marginBottom: 4 }}>
              {asArray(r?.target_classes).map((c) => <span key={c} className="er-chip">{c}</span>)}
            </div>
          )}
          {r?.rationale && (
            <div className="er-sched-section">
              <div className="er-sched-section-label">调度合理性</div>
              <div className="er-sched-body">{r.rationale}</div>
            </div>
          )}
          {asArray(r?.improvement_directions).length > 0 && (
            <div className="er-sched-section">
              <div className="er-sched-section-label">建议提升的具体方向</div>
              <div className="er-sched-body">{asArray(r?.improvement_directions).join('；')}</div>
            </div>
          )}
          <div className="er-sched-nums">
            {r?.predicted_coverage && <div><b>{r.predicted_coverage}</b>预计覆盖</div>}
            {r?.resource_efficiency && <div>{r.resource_efficiency}</div>}
          </div>
        </div>
      ))}

      {quarters.length > 0 && (
        <div className="er-card" style={{ background: '#f8fafc', marginTop: 24 }}>
          <h3 className="er-h3">本期培训资源 · 节奏建议</h3>
          <table className="er-q-table">
            <thead><tr><th>季度</th><th>建议执行</th></tr></thead>
            <tbody>
              {quarters.map((q, i) => (
                <tr key={i}><td><b>{q?.quarter}</b></td><td>{asArray(q?.items).join('、')}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── ⑤ 各班级专题点评（仅 R2）────────────────────────────────────────────────

function Commentaries({ scheduling }) {
  const rows = Array.isArray(scheduling?.class_commentaries) ? scheduling.class_commentaries : [];
  if (!rows.length) return null;
  return (
    <div className="er-page">
      <div className="er-page-mark">PART 04 · 各班级专题点评</div>
      <h2 className="er-h2">各班级专题点评</h2>
      <p className="er-muted" style={{ marginBottom: 18, fontSize: 13 }}>
        从企业视角对每个班级单独点评，便于整体把握。各班详细数据请下载对应班级报告。
      </p>
      {rows.map((c, i) => (
        <div key={i} className="er-commentary">
          <div className="er-commentary-head">
            <h3 className="er-commentary-name">{c?.class_name}</h3>
            <div className="er-commentary-meta">{c?.headcount} 人 · 团队均分 {c?.team_avg ?? '—'}</div>
          </div>
          <div className="er-commentary-text">{c?.commentary}</div>
        </div>
      ))}
    </div>
  );
}

// ── ⑥ 封底 ─────────────────────────────────────────────────────────────────

function Footer({ payload }) {
  const sched = payload?.scheduling || {};
  const meta = sched.ai_meta || {};
  const ent = payload?.cover?.enterprise_name || '';
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="er-page er-footer-page">
      <div className="er-seal">END OF REPORT</div>
      <div className="er-footer-divider" />
      <p style={{ margin: '0 0 8px' }}><b style={{ color: '#0f172a' }}>关于本报告</b></p>
      <p style={{ margin: '0 0 8px' }}>
        本报告由前哨 AI 领导力测评系统基于各班级已完成测评学员的数据聚合生成；班级矩阵 / 全员分布为系统聚合，调度建议部分由 AI 基于共性短板生成。
      </p>
      <p style={{ margin: '0 0 8px' }}>
        报告版本：<b>企业版</b>
        {meta.prompt_version != null && ` · prompt v${meta.prompt_version}`}
        {' · '}报告生成时间：<b>{formatBeijingTime(sched.generated_at)}</b>
      </p>
      {ent && <p style={{ margin: '0 0 8px' }}>企业：<b>{ent}</b></p>}
      <div className="er-footer-divider" />
      <p style={{ margin: 0, fontSize: 11, color: '#94a3b8' }}>
        © {today.slice(0, 4)} 前哨科技有限公司 · 本报告含跨班级聚合数据，请按企业合规策略保管
      </p>
    </div>
  );
}

export default function EnterpriseReport({ payload, printMode }) {
  const p = payload || {};
  const isCrossClass = p.scope === 'enterprise';
  return (
    <div className={`er-root${printMode ? ' er-print' : ''}`}>
      <Cover cover={p.cover} />
      <Portrait overview={p.overview} scheduling={p.scheduling} />
      {isCrossClass && <CrossClass overview={p.overview} />}
      <Scheduling scheduling={p.scheduling} isCrossClass={isCrossClass} />
      {isCrossClass && <Commentaries scheduling={p.scheduling} />}
      <Footer payload={p} />
    </div>
  );
}
